import { useState, useEffect } from 'react';

export type Theme = 'light' | 'dark' | 'cyberpunk' | 'edge';

export function useTheme() {
  // Initialize theme from localStorage or system preference
  const getInitialTheme = (): Theme => {
    const savedTheme = localStorage.getItem('getdeleto-theme') as Theme | null;
    
    if (savedTheme && ['light', 'dark', 'cyberpunk', 'edge'].includes(savedTheme)) {
      return savedTheme;
    }
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  };
  
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  
  // Apply theme to document
  useEffect(() => {
    // Remove all theme classes first
    document.documentElement.classList.remove('dark', 'cyberpunk', 'edge');
    
    // Add the appropriate class for the selected theme
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (theme === 'cyberpunk') {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.add('cyberpunk');
    } else if (theme === 'edge') {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.add('edge');
    }
    
    // Save to localStorage
    localStorage.setItem('getdeleto-theme', theme);
  }, [theme]);
  
  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      // Only change if user hasn't manually set a preference
      if (!localStorage.getItem('getdeleto-theme')) {
        setTheme(mediaQuery.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  const toggleTheme = () => {
    setTheme(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'cyberpunk';
      if (prev === 'cyberpunk') return 'edge';
      return 'light'; // edge → light
    });
  };
  
  const setThemeDirectly = (newTheme: Theme) => {
    setTheme(newTheme);
  };
  
  return { theme, toggleTheme, setThemeDirectly };
} 